import HistorialCambios from "components/HistorialCambios";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useContext, useEffect, useState, useCallback, useMemo } from "react";

// Components
import { Card, Grid, Icon, Menu, MenuItem } from "@mui/material";
import Footer from "components/Footer";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import DataTable from "components/Tables/DataTable";
import SoftButton from "components/SoftButton";
import CustomPagination from "components/Pagination";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import ModalDelete from "components/Modals/Delete";

import { MessageManager } from "context";
import { UserContext } from "context/user";
import { getColor } from "utils/colors";
import ModalEmpresa from "./components/ModalEmpresa";
import dataEmpresa from "./data/dataEmpresa";
import ModalResponsable from "./components/ModalResponsable";

// Hook de debounce
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

function Empresas() {
  const [rows, setRows] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 500);

  const [openMenu, setOpenMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openModalAdd, setOpenModalAdd] = useState(false);
  const [openModalResponsable, setOpenModalResponsable] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [page, setPage] = useState(1);
  const [pagesInfo, setPagesInfo] = useState({ pages: 0, total: 0 });
  const [orderBy, setOrderBy] = useState({ campo: "razonSocial", orden: "ASC" });

  const { handleSnackbar } = useContext(MessageManager);
  const { user, verificarAcceso } = useContext(UserContext);
  const { columns, getRows } = dataEmpresa;

  // Funciones con useCallback
  const handleOrderBy = useCallback((campo) => {
    setOrderBy((prev) => ({
      campo,
      orden: prev.campo === campo && prev.orden === "ASC" ? "DESC" : "ASC",
    }));
  }, []);

  const handleOpenMenu = useCallback((event, item) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
    setSelectedEmpresa(item);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
    setOpenMenu(false);
  }, []);

  const handleSearch = (event) => {
    setSearchInput(event.target.value);
  };

  const [deleteEmpresa, { loading: deleting }] = useMutation(
    gql`
      mutation deleteEmpresa($id: ID!) {
        deleteEmpresa(id: $id)
      }
    `
  );
  const [resendEmpresaCredentials] = useMutation(gql`
    mutation resendEmpresaCredentials($id: ID!) {
      resendEmpresaCredentials(id: $id)
    }
  `);
  const [saveEstado, { loading: cambiandoEstado }] = useMutation(gql`
    mutation cambiarEstadoEmpresa($input: EmpresaInput!) {
      saveEmpresa(input: $input) { id estado }
    }
  `);

  const handleEstado = async () => {
    if (user?.tipoUser !== "Administrador" || cambiandoEstado) return;
    const estado = selectedEmpresa.estado === "Irregular" ? "Regular" : "Irregular";
    handleCloseMenu();
    try {
      await saveEstado({ variables: { input: { id: selectedEmpresa.id, estado } } });
      await refetch();
      handleSnackbar(`Empresa marcada como ${estado.toLowerCase()}`, "success");
    } catch (e) {
      handleSnackbar(e.message || "No se pudo cambiar el estado de la empresa", "error");
    }
  };

  const handleResendCredentials = async () => {
    try {
      const { data } = await resendEmpresaCredentials({ variables: { id: selectedEmpresa.id } });
      handleSnackbar(
        data.resendEmpresaCredentials
          ? `Credenciales enviadas a ${selectedEmpresa.email}`
          : "No se pudo enviar el correo; el error quedó registrado",
        data.resendEmpresaCredentials ? "success" : "error"
      );
    } catch (e) {
      handleSnackbar(e.message || "No se pudieron reenviar las credenciales", "error");
    }
  };

  const { loading, error, data, refetch } = useQuery(
    gql`
      query getEmpresas($search: String, $page: Int, $order: Order) {
        paginationInfo {
          pages
          total
        }
        empresas(search: $search, pagination: { page: $page, limit: 10 }, order: $order) {
          id
          razonSocial
          cuit
          email
          gerente
          actividad
          estado
          responsableTecnico {
            id
            nombre
            apellido
            email
            matricula
          }
        }
      }
    `,
    {
      variables: {
        search: search || null,
        page: page,
        order: { field: orderBy.campo, order: orderBy.orden },
      },
      fetchPolicy: "no-cache",
    }
  );

  // useMemo para recalcular las filas solo cuando data o la función getRows cambien
  const memoizedRows = useMemo(() => {
    if (data) {
      return getRows(data.empresas, handleOpenMenu);
    }
    return [];
  }, [data, getRows, handleOpenMenu]);

  useEffect(() => {
    if (data) {
      setRows(memoizedRows);
      setPagesInfo(data.paginationInfo);
    }
  }, [data, memoizedRows]);

  const handleDeleteEmpresa = async (id) => {
    try {
      await deleteEmpresa({ variables: { id } });
      refetch();
      handleSnackbar("Empresa eliminada correctamente", "success");
    } catch (e) {
      handleSnackbar(e.message || "Error al eliminar la empresa", "error");
    }
  };

  // Render del menú contextual
  const renderMenu = (
    <Menu id="simple-menu" anchorEl={anchorEl} open={Boolean(openMenu)} onClose={handleCloseMenu}>
      {user?.tipoUser === "Administrador" && (
        <MenuItem disabled={cambiandoEstado} onClick={handleEstado}>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" sx={{ width: "100%" }}>
            <SoftTypography variant="p">
              {selectedEmpresa?.estado === "Irregular" ? "Marcar como regular" : "Marcar como irregular"}
            </SoftTypography>
            <Icon fontSize="small" sx={{ verticalAlign: "middle", color: getColor("gobSecondary") }}>
              {selectedEmpresa?.estado === "Irregular" ? "lock_open" : "lock"}
            </Icon>
          </SoftBox>
        </MenuItem>
      )}
      <MenuItem disabled={!verificarAcceso(223)}
        onClick={() => {
          setOpenModalResponsable(true);
          handleCloseMenu();
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <SoftTypography variant="p">Responsable técnico</SoftTypography>
          <Icon fontSize="small" sx={{ verticalAlign: "middle", color: getColor("gobSecondary") }}>
            hail
          </Icon>
        </SoftBox>
      </MenuItem>
      <MenuItem disabled={!verificarAcceso(223)}
        onClick={() => {
          setOpenModalAdd(true);
          handleCloseMenu();
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <SoftTypography variant="p">Editar</SoftTypography>
          <Icon fontSize="small" sx={{ verticalAlign: "middle", color: getColor("gobSecondary") }}>
            edit
          </Icon>
        </SoftBox>
      </MenuItem>
      <MenuItem disabled={!verificarAcceso(245)}
        onClick={() => {
          handleResendCredentials();
          handleCloseMenu();
        }}
      >
        <SoftBox display="flex" justifyContent="space-between" sx={{ width: "100%" }}>
          <SoftTypography variant="p">Reenviar credenciales</SoftTypography>
          <Icon fontSize="small" color="primary">email</Icon>
        </SoftBox>
      </MenuItem>
      <MenuItem disabled={!verificarAcceso(224) || deleting} onClick={() => {
        setOpenModalDelete(true);
        handleCloseMenu();
      }}>
        <SoftBox display="flex" justifyContent="space-between" sx={{ width: "100%" }}>
          <SoftTypography variant="p">Eliminar</SoftTypography>
          <Icon fontSize="small" color="error">delete</Icon>
        </SoftBox>
      </MenuItem>
    <HistorialCambios modulo="empresas" id={selectedEmpresa?.id} nombre={selectedEmpresa?.razonSocial} menu />
    </Menu>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox>
          <Card>
            <Grid container spacing={2} p={3}>
              <Grid item xs={12}>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                  <SoftTypography variant="h6" fontWeight="bold">
                    Empresas
                  </SoftTypography>
                  <SoftBox display="flex" alignItems="center" gap={2}>
                    <SoftInput
                      placeholder="Buscar..."
                      icon={{ component: "search", direction: "left" }}
                      onChange={handleSearch}
                      value={searchInput}
                    />
                    <SoftButton
                      disabled={!verificarAcceso(222)}
                      color="primary"
                      onClick={() => {
                        setSelectedEmpresa(null);
                        setOpenModalAdd(true);
                      }}
                      startIcon={<Icon>add</Icon>}
                    >
                      Nueva
                    </SoftButton>
                  </SoftBox>
                </SoftBox>
              </Grid>
            </Grid>
            <SoftBox>
              {loading ? (
                <Loading />
              ) : rows?.length > 0 ? (
                <DataTable columns={columns} rows={rows} order={orderBy} setOrder={handleOrderBy} />
              ) : (
                <SinDatos />
              )}
            </SoftBox>
            <CustomPagination
              loading={loading}
              length={rows.length}
              total={pagesInfo?.total}
              pages={pagesInfo?.pages}
              page={page}
              setPage={setPage}
              noCard
            />
          </Card>
          {renderMenu}
          <ModalDelete open={openModalDelete} handleClose={() => setOpenModalDelete(false)} nombre={`la empresa ${selectedEmpresa?.razonSocial || ""}`} functionDelete={() => handleDeleteEmpresa(selectedEmpresa.id)} />
          <ModalEmpresa
            open={openModalAdd}
            handleClose={() => setOpenModalAdd(false)}
            refetch={refetch}
            idEmpresa={selectedEmpresa?.id}
          />
          <ModalResponsable
            open={openModalResponsable}
            handleClose={() => setOpenModalResponsable(false)}
            refetch={refetch}
            idEmpresa={selectedEmpresa?.id}
            currentResponsableId={selectedEmpresa?.responsableTecnico?.id}
          />
        </SoftBox>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Empresas;
